import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  ChangeDetectorRef,
  SimpleChanges,
  Output,
  EventEmitter,
  ViewChildren,
  QueryList,
} from '@angular/core';
import {
  UntypedFormGroup,
  UntypedFormBuilder,
  UntypedFormArray,
  AbstractControl,
  UntypedFormControl,
  Validators,
  ValidatorFn,
  ValidationErrors,
} from '@angular/forms';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';

// COMPONENTS

// CONFIG

// SERVICES
import { LegacyPageEvent as PageEvent } from '@angular/material/legacy-paginator';
import { MatLegacySelect as MatSelect } from '@angular/material/legacy-select';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { tap, takeUntil, catchError } from 'rxjs/operators';

// INTERFACES
import { DefaultShipToAddress } from '@app/core/_constant/purchase-order.constant';
import { SettingsService } from '@app/core/settings/services/settings.service';

import { IBinLocation } from '../../../location/interfaces/location.interface';
import { ILot } from '../../../lot';
import { IRfqLot } from '../../../rfq/interfaces/rfq-lot.interface';
import {
  PaginationConfig,
  PurchaseOrderApiService,
  SnackBarConfig,
  LotApiService,
  OptionInterface,
  LocationApiService,
  IWarehouse,
} from '../../../shared';
import { trackByControlValue, trackById } from '../../../shared/util';
import {
  IPurchaseOrder,
  IPurchaseOrderItem,
  IReceivedPerRfqItem,
  PurchaseOrderItemField,
  IPurchaseOrderItemBulkUpdateDetails,
} from '../../interfaces';
import { PurchaseOrderItemFormComponent } from '../purchase-order-item-form/purchase-order-item-form.component';

import { DialogConfig } from './../../../shared/configs/dialog.config';
import { UiConfirmationDialogComponent } from './../../../ui/components/ui-confirmation-dialog/ui-confirmation-dialog.component';

@Component({
  selector: 'lum-purchase-order-items',
  templateUrl: './purchase-order-items.component.html',
  styleUrls: ['./purchase-order-items.component.scss'],
})
export class PurchaseOrderItemsComponent implements OnInit, OnDestroy {
  @ViewChildren('matSelectComponent')
  matSelectComponent :QueryList<MatSelect>;

  @Input()
  orders :IPurchaseOrder[] = [];

  @Input()
  newCreatedLot :any = {};

  @Input()
  newBulkCreatedLot :any[] = [];

  @Input()
  ftLotTrackingPageEnabled :boolean;

  @Input()
  ftUniqueLotNumberPerSkuEnabled :boolean;

  @Input()
  filters :Record<string, any> = {};

  @Output()
  refreshOrdersEvent = new EventEmitter<boolean>();

  @Output()
  bulkReceiveEvent = new EventEmitter<any>();

  @Output()
  openCreateLotDialogEvent = new EventEmitter<{
    rfqId ?:number;
    purchaseOrderId ?:number;
    rfqIds ?:number[];
    isBulkUpdate ?:boolean;
  }>();

  form :UntypedFormGroup;
  private onDestroy$ = new Subject();
  trackByControlValue = trackByControlValue;
  trackById = trackById;
  pagination = {
    total: 0,
    per_page: PaginationConfig.perPage,
    current_page: PaginationConfig.page,
  };
  paginationConfig = PaginationConfig;
  lineItems :UntypedFormGroup[] = [];
  purchaseOrderItemField = PurchaseOrderItemField;
  loading$ :Observable<boolean>;
  activeBulkEdit :boolean = false;

  isLotsLoading :boolean = false;

  openCreateLotDialogIndex :number;
  inventorySettings :any;
  inventorySettings$ :Observable<any[]>;
  pickingWarehouse :IWarehouse;
  defaultPickingWarehouseLocation :IBinLocation;

  currentRfqId :number;
  lotsByRfqId :{
    [rfqId :string] :{
      id :number;
      rfq_id :number;
      lot_number :string;
      manufacturer_code :string;
      original_quantity :number;
      production_date :Date;
      expiry_date :Date;
    }[];
  } = {};

  constructor(
    private dialog :MatDialog,
    private fb :UntypedFormBuilder,
    private cd :ChangeDetectorRef,
    private purchaseOrderService :PurchaseOrderApiService,
    private lotApiService :LotApiService,
    private router :Router,
    private snackBar :MatSnackBar,
    private settingsSvc :SettingsService,
    private locationApiService :LocationApiService,
  ) {}

  ngOnInit() :void {
    this.initForm();
    this.loadDataFromService();
    this.initInventorySettings();
    this.getFromService();
  }

  ngOnChanges({ orders, newCreatedLot, newBulkCreatedLot } :SimpleChanges) :void {
    if (newCreatedLot && !newCreatedLot.isFirstChange()) {
      this.handleNewCreatedLotChange(newCreatedLot.currentValue);
    }

    if (newBulkCreatedLot && !newBulkCreatedLot.isFirstChange()) {
      Object.values(newBulkCreatedLot.currentValue).forEach((newLot :any) => {
        this.handleBulkCreatedLot(newLot);
      });
    }

    if (orders && !orders.isFirstChange()) {
      this.initForm();
    }
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  handleBulkCreatedLot(newLot :any) :void {
    const rfqId = newLot.rfq_id;
    const newLotId = newLot.id;

    if (!this.lotsByRfqId[rfqId]) {
      this.lotsByRfqId[rfqId] = [];
    }

    const isUnique = !this.lotsByRfqId[rfqId].some((lot) => lot.id === newLotId);
    if (isUnique) {
      this.lotsByRfqId[rfqId].push(newLot);
    }

    this.checkedAndlotTrackingItems.forEach((lineItem, index) => {
      const rfqControl = lineItem.get('rfqId');
      if (rfqControl && rfqControl.value === rfqId) {
        const lotIdControl = lineItem.get('lotId');
        const selectedLotControl = lineItem.get('selectedLot');
        if (lotIdControl && selectedLotControl) {
          lineItem.patchValue({
            lotId: newLotId,
            selectedLot: {
              value: newLotId,
              label: newLot.lot_number,
              subLabel: '',
            },
            lot: newLot,
          });
          lotIdControl.markAsDirty();
        }
      }
    });

    this.cd.detectChanges();
  }

  handleNewCreatedLotChange(newCreatedLot :any) :void {
    const rfqId = newCreatedLot.rfq_id;
    const newLotId = newCreatedLot.id;

    if (!this.lotsByRfqId[rfqId]) {
      this.lotsByRfqId[rfqId] = [];
    }

    const isUnique = !this.lotsByRfqId[rfqId].some((lot) => lot.id === newLotId);
    if (isUnique) {
      this.lotsByRfqId[rfqId].push(newCreatedLot);
    }

    this.updateSelectedItem(this.openCreateLotDialogIndex, newCreatedLot);
  }

  onBulkEdit() :void {
    this.dialog
      .open(PurchaseOrderItemFormComponent, {
        width: DialogConfig.sizes.small,
        data: {},
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result :IPurchaseOrderItemBulkUpdateDetails) => {
        if (result) {
          this.checkedLineItems.forEach((lineItem :AbstractControl) => {
            if (result.purchasePrice !== undefined && result.purchasePrice >= 0) {
              lineItem.get('purchasePrice')?.markAsDirty();
            }
            if (result.purchaseQty !== undefined && result.purchaseQty > 0) {
              lineItem.get('purchaseQty')?.markAsDirty();
            }
            if (
              result.addToReceivingQty !== undefined &&
              result.addToReceivingQty >= 0
            ) {
              if (result.addToReceivingQty > 0) {
                lineItem.get('addToReceivingQty')?.markAsDirty();
              } else {
                lineItem.get('addToReceivingQty')?.markAsPristine();
              }
            }

            lineItem.patchValue({
              ...(result.purchasePrice !== undefined &&
                result.purchasePrice >= 0 && {
                  purchasePrice: result.purchasePrice || 0,
                }),
              ...(result.purchaseQty !== undefined &&
                result.purchaseQty > 0 && {
                  purchaseQty: result.purchaseQty || 1,
                }),
              ...(result.addToReceivingQty !== undefined &&
                result.addToReceivingQty >= 0 && {
                  addToReceivingQty: result.addToReceivingQty || 0,
                }),
            });
          });
          this.cd.detectChanges();
        }
      });
  }

  onReset() :void {
    this.dialog
      .open(UiConfirmationDialogComponent, {
        data: {
          title: 'Confirm Reset',
          message:
            'Are you sure you want to reset the changes you made to these items?',
        },
        width: DialogConfig.sizes.small,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result) => {
        if (result) {
          this.initForm();
        }
      });
  }

  onSubmit() :void {
    const data = this.dirtyValidLineItems.map((lineItem :AbstractControl) => {
      return {
        poId: lineItem.value.poId || 0,
        poItemId: lineItem.value.poItemId,
        sku: lineItem.value.sku,
        purchasePrice: lineItem.value.purchasePrice || 0,
        lotId: lineItem.value.lotId,
        purchaseQty: lineItem.value.purchaseQty || 0,
        addToReceivingQty: lineItem.value.addToReceivingQty || 0,
      };
    });

    this.purchaseOrderService
      .updateLineItems({
        items: data,
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response :IPurchaseOrderItem[]) => {
          this.snackBar.open('Successfully updated line items', 'Success', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
          if (this.rrLineItemCount <= 0) {
            this.refreshOrdersEvent.emit(true);
          } else {
            this.bulkReceiveEvent.emit(response);
            this.router.navigate(['inventory/receiving']);
          }
        },
        error: (response) => {
          this.snackBar.open(response.error, 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  onAddToReceiving() :void {
    const data = this.checkedLineItems
      .map((lineItem :AbstractControl) => {
        const purchaseOrder = this.orders.find(
          (order) => order.id === lineItem.value.poId,
        );
        const purchaseOrderItem = purchaseOrder?.purchase_order_items?.find(
          (item) => item.id === lineItem.value.poItemId,
        );

        if (purchaseOrder && purchaseOrderItem) {
          const { purchase_order_items, ...newPurchaseOrder } = purchaseOrder;
          const addToReceiveQty =
            lineItem.value.purchaseQty - lineItem.value.receivedQty;

          return {
            id: purchaseOrderItem?.id || 0,
            purchaseOrderId: purchaseOrder?.id || 0,
            internalSku: purchaseOrderItem?.internal_sku || '',
            productName: purchaseOrderItem?.product_name || '',
            rfqId: purchaseOrderItem?.rfq_id || 0,
            quantity: purchaseOrderItem?.quantity || 0,
            addToReceiveQty: addToReceiveQty >= 0 ? addToReceiveQty : 0,
            unit: purchaseOrderItem?.unit || '',
            unitPrice: purchaseOrderItem?.unit_price || 0,
            orderedUomId: purchaseOrderItem?.ordered_uom_id || 0,
            rfq: this.convertUnderscoreToCamelCase(purchaseOrderItem?.rfq) || {},
            purchaseOrder:
              this.convertUnderscoreToCamelCase(newPurchaseOrder) || {},
            warehouse:
              purchaseOrder?.warehouse ??
              purchaseOrderItem?.rfq?.default_receiving_bin?.parent ??
              this.pickingWarehouse ??
              null,
            location:
              purchaseOrder.default_ship_to_address ===
              DefaultShipToAddress.WAREHOUSE
                ? purchaseOrderItem?.rfq?.default_receiving_bin ||
                  purchaseOrder?.warehouse?.default_receiving_location
                : (purchaseOrderItem?.rfq?.default_receiving_bin ??
                  this.defaultPickingWarehouseLocation ??
                  null),
            lotId: lineItem?.value?.lotId || 0,
            lot: lineItem?.value?.lot || null,
          };
        }

        return null;
      })
      .filter((item) => item);

    this.bulkReceiveEvent.emit(data);
    this.router.navigate(['inventory/receiving']);
  }

  onEditLineItem(fieldName :string = '', index :number = 0) :void {
    const lineItem = this.lineItemsFormArray.controls[index] as UntypedFormControl;
    if (fieldName === PurchaseOrderItemField.PURCHASE_PRICE) {
      lineItem.patchValue({
        activePurchasePriceField: !lineItem.value.activePurchasePriceField,
      });
    }

    if (fieldName === PurchaseOrderItemField.PURCHASE_QTY) {
      lineItem.patchValue({
        activePurchaseQtyField: !lineItem.value.activePurchaseQtyField,
      });
    }

    if (fieldName === PurchaseOrderItemField.ADD_TO_RECEIVING_QTY) {
      lineItem.patchValue({
        activeAddToReceivingQtyField: !lineItem.value.activeAddToReceivingQtyField,
      });
    }

    if (fieldName === PurchaseOrderItemField.LOT_ID) {
      lineItem.patchValue({
        activeLotIdField: !lineItem.value.activeLotIdField,
      });
    }
  }

  onPaginationEvent(pagination :PageEvent) :void {
    this.pagination.current_page = pagination.pageIndex + 1;
    this.pagination.per_page = pagination.pageSize;
    this.form = this.fb.group({
      lineItems: this.fb.array(this.lineItems),
    });
  }

  onCheckedAllLineItems(checked :boolean = false) :void {
    this.paginatedLineItems.forEach((lineItem) => {
      lineItem.patchValue({
        checked: checked,
      });
    });
  }

  onBulkInlineEdit() :void {
    this.activeBulkEdit = !this.activeBulkEdit;
    this.lineItemsFormArray.controls.forEach((lineItem :AbstractControl) => {
      lineItem.patchValue({
        activePurchasePriceField: this.activeBulkEdit,
        activePurchaseQtyField: this.activeBulkEdit,
        activeAddToReceivingQtyField: this.activeBulkEdit,
        activeLotIdField: this.activeBulkEdit,
      });
    });
  }

  onInput(eventValue :string, index :number = 0) {
    const value = Number(eventValue);
    const lineItem = this.lineItemsFormArray.controls[index] as UntypedFormControl;

    if (lineItem && (value === undefined || value === 0)) {
      lineItem.get('addToReceivingQty')?.markAsPristine();
    }
  }

  private initForm() :void {
    this.lineItems = [];
    if (this.orders?.length > 0) {
      this.orders?.forEach((lineItem :IPurchaseOrder) => {
        if (lineItem.purchase_order_items?.length > 0) {
          lineItem.purchase_order_items?.forEach(
            (purchaseOrderItem :IPurchaseOrderItem) => {
              const totalReceived = lineItem.total_receive_per_rfqs?.find(
                (receivedRfq :IReceivedPerRfqItem) =>
                  receivedRfq.purchase_order_rfq_id === purchaseOrderItem.id,
              );

              let productFilter = '';
              let skuFilter = '';
              if (
                this.filters !== null &&
                this.filters !== undefined &&
                Object.keys(this.filters).length > 0
              ) {
                productFilter = this.filters.hasOwnProperty('product')
                  ? this.filters['product']
                  : '';
                skuFilter = this.filters.hasOwnProperty('sku')
                  ? this.filters['sku']
                  : '';
              }

              // Check if the current SKU is in the filter list
              if (!this.isSkuInFilterList(purchaseOrderItem.internal_sku)) {
                return; // Skip to the next iteration if SKU is not in the array
              }

              const emptyOrHasSpaces =
                this.isEmptyOrHasSpaces(productFilter) &&
                this.isEmptyOrHasSpaces(skuFilter);
              const productOrSkuExists = this.checkIfProductOrSkuExists(
                purchaseOrderItem?.product_name,
                purchaseOrderItem?.internal_sku,
              );

              if (emptyOrHasSpaces || productOrSkuExists) {
                this.lineItems.push(
                  this.fb.group({
                    poItemId: purchaseOrderItem.id || 0,
                    supplierName: lineItem.factory?.factory_name || '',
                    poId: lineItem.id || 0,
                    sku: purchaseOrderItem.internal_sku || '',
                    productName: purchaseOrderItem.product_name || '',
                    purchasePrice: [
                      purchaseOrderItem.unit_price || 0,
                      [this.validatePurchasePrice.bind(this)],
                    ],
                    purchaseQty: [
                      purchaseOrderItem.quantity || 0,
                      [this.validatePurchaseQty.bind(this)],
                    ],
                    receivedQty: totalReceived?.quantity
                      ? totalReceived?.quantity
                      : 0,
                    origPurchasePrice: purchaseOrderItem.unit_price || 0,
                    origPurchaseQty: purchaseOrderItem.quantity || 0,
                    addToReceivingQty: [0, [this.validateAddToReceived.bind(this)]],
                    lotId: purchaseOrderItem.lot_id,
                    rfqId: purchaseOrderItem.rfq_id,
                    selectedLot: {
                      value: purchaseOrderItem?.lot?.id || '',
                      label: purchaseOrderItem?.lot?.lot_number || '',
                      subLabel: purchaseOrderItem?.lot?.production_date || '',
                    },
                    lot: purchaseOrderItem?.lot,
                    hasLotTracking: purchaseOrderItem?.rfq?.lot_tracking || false,
                    activePurchasePriceField: false,
                    activePurchaseQtyField: false,
                    activeAddToReceivingQtyField: false,
                    activeLotIdField: false,
                    uom: purchaseOrderItem?.ordered_unit_of_measure?.name || '',
                    checked: false,
                  }),
                );
              }
            },
          );
        }
      });
    }

    this.form = this.fb.group({
      lineItems: this.fb.array(this.lineItems),
    });

    this.pagination.current_page = PaginationConfig.page;
    this.pagination.per_page = PaginationConfig.perPage;
    this.pagination.total = this.lineItems.length;
    this.cd.detectChanges();
  }

  onOpenLotListEvent(opened :boolean, index :number) :void {
    if (!opened) {
      return;
    }

    const rfqId = this.lineItemsFormArray.controls[index]?.get('rfqId')?.value;
    this.currentRfqId = rfqId;

    this.isLotsLoading = true;

    this.lotApiService
      .getByRfqIds({
        rfqIds: [this.currentRfqId.toString()],
        limit: '1000',
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response :any) => {
          this.lotsByRfqId[rfqId] = response.data;
          this.isLotsLoading = false;
          this.cd.detectChanges();
        },
        error: (response :any) => {
          this.isLotsLoading = false;
        },
      });
  }

  openCreateLotDialog(index :number) :void {
    this.openCreateLotDialogIndex = index;
    const purchaseOrderId =
      this.lineItemsFormArray.controls[index]?.get('poId')?.value;

    this.openCreateLotDialogEvent.emit({
      rfqId: this.currentRfqId,
      purchaseOrderId: purchaseOrderId,
    });
  }

  openCreateLotNewDialog(index :number, item :any) :void {
    this.openCreateLotDialogIndex = index;
    this.openCreateLotDialogEvent.emit({
      rfqId: item.rfqId,
      purchaseOrderId: item.poId,
    });
  }

  openBulkCreateLotDialog() :void {
    const rfqIds :number[] = [];

    this.checkedAndlotTrackingItems.forEach((lineItem :AbstractControl) => {
      rfqIds.push(lineItem.get('rfqId')?.value);
    });

    this.openCreateLotDialogEvent.emit({
      rfqIds: rfqIds,
      isBulkUpdate: true,
    });
  }

  onSelectLotEvent(
    event :any,
    index :number,
    oldLotId :number,
    matSelect :MatSelect,
  ) :void {
    const lotId = event.value;
    const currentLots = this.lotsByRfqId[this.currentRfqId];

    if (!this.hasLots(currentLots)) {
      this.handleNoLotsCase(index, matSelect, oldLotId);

      return;
    }

    const oldSelectedLot = this.findLotById(currentLots, oldLotId);

    if (lotId === 'createANewLot') {
      this.handleCreateNewLotCase(index, oldSelectedLot, matSelect);

      return;
    }

    const selectedLot = this.findLotById(currentLots, lotId);

    if (selectedLot) {
      this.updateSelectedItem(index, selectedLot);
    }
  }

  onSelectLotNewEvent(selectedLot :IRfqLot | null, index :number) :void {
    this.updateSelectedItem(index, selectedLot);
  }

  private hasLots(lots :any[]) :boolean {
    return lots && lots.length > 0;
  }

  private handleNoLotsCase(
    index :number,
    matSelect :MatSelect,
    oldLotId :number,
  ) :void {
    this.openCreateLotDialog(index);
    this.restoreMatSelectValue(matSelect, oldLotId);
    this.cd.detectChanges();
  }

  private handleCreateNewLotCase(
    index :number,
    oldSelectedLot :any,
    matSelect :MatSelect,
  ) :void {
    this.openCreateLotDialog(index);

    if (oldSelectedLot) {
      this.updateItemWithOldLot(index, oldSelectedLot);
      this.restoreMatSelectValue(matSelect, oldSelectedLot.id);
    } else {
      this.clearItemLot(index);
      this.restoreMatSelectValue(matSelect, null);
    }

    this.cd.detectChanges();
  }

  private clearItemLot(index :number) :void {
    this.lineItemsFormArray.at(index).patchValue({
      lotId: '',
      selectedLot: {
        value: '',
        label: '',
        subLabel: '',
      },
    });
  }

  private updateItemWithOldLot(index :number, oldSelectedLot :any) :void {
    this.lineItemsFormArray.at(index).patchValue({
      lotId: oldSelectedLot.id,
      selectedLot: {
        value: oldSelectedLot.id,
        label: oldSelectedLot.lot_number,
        subLabel: '',
      },
    });
  }

  private updateSelectedItem(index :number, selectedLot :any) :void {
    this.lineItemsFormArray.at(index).patchValue({
      lotId: selectedLot?.id || null,
      selectedLot: {
        value: selectedLot?.id || '',
        label: selectedLot?.lot_number || '',
        subLabel: '',
      },
      lot: selectedLot,
    });

    this.lineItemsFormArray.controls[index]?.get('lotId')?.markAsDirty();
    this.cd.detectChanges();
  }

  private findLotById(lots :any[], id :number) :any {
    return lots.find((lot) => lot.id === id);
  }

  private restoreMatSelectValue(matSelect :MatSelect, value :number | null) :void {
    if (matSelect) {
      matSelect.writeValue(value);
      matSelect._onChange(value);
      matSelect.close();
    }
  }

  private isSkuInFilterList(sku :string) :boolean {
    if (this.filters?.['skus']) {
      const skusArray = this.filters['skus'].toLowerCase().split(','); // Convert to uppercase and split

      return skusArray.includes(sku.toLowerCase()); // Convert SKU to uppercase before checking
    }

    return true; // Return true if 'skus' filter is not available
  }

  private checkIfProductOrSkuExists(
    productName :string = '',
    internalSku :string = '',
  ) :boolean {
    let productFilter = '';
    let skuFilter = '';
    if (
      this.filters !== null &&
      this.filters !== undefined &&
      Object.keys(this.filters).length > 0
    ) {
      productFilter = this.filters.hasOwnProperty('product')
        ? this.filters['product']
        : '';
      skuFilter = this.filters.hasOwnProperty('sku') ? this.filters['sku'] : '';
    }

    const productHasValue = !this.isEmptyOrHasSpaces(productFilter);
    const skuHasValue = !this.isEmptyOrHasSpaces(skuFilter);

    if (productHasValue && skuHasValue) {
      return (
        productName
          ?.toLowerCase()
          .includes(this.filters['product']?.toLowerCase()) &&
        internalSku?.toLowerCase().includes(this.filters['sku']?.toLowerCase())
      );
    } else if (productHasValue) {
      return productName
        ?.toLowerCase()
        .includes(this.filters['product']?.toLowerCase());
    } else if (skuHasValue) {
      return internalSku
        ?.toLowerCase()
        .includes(this.filters['sku']?.toLowerCase());
    }

    return false;
  }

  private validatePurchasePrice(control :AbstractControl) :ValidationErrors | null {
    const purchasePrice = control.value;

    if (!control.dirty) {
      return null;
    }
    if (purchasePrice === null || typeof purchasePrice === 'string') {
      return { purchasePrice: 'Invalid value for Purchase Price' };
    }
    if (typeof purchasePrice !== 'number') {
      return { purchasePrice: 'Purchase Price must be a number' };
    }
    if (purchasePrice < 0) {
      return {
        purchasePrice: 'Purchase Price cannot be less than 0',
      };
    }

    return null;
  }

  private validatePurchaseQty(control :AbstractControl) :ValidationErrors | null {
    const purchaseQty = control.value;

    if (!control.dirty) {
      return null;
    }
    if (purchaseQty === null || typeof purchaseQty === 'string') {
      return { purchaseQty: 'Invalid value for Purchase Quantity' };
    }
    if (typeof purchaseQty !== 'number') {
      return { purchaseQty: 'Purchase Quantity must be a number' };
    }
    if (purchaseQty < 1) {
      return {
        purchaseQty: 'Purchase Quantity cannot be less than 1',
      };
    }

    return null;
  }

  private validateAddToReceived(control :AbstractControl) :ValidationErrors | null {
    const addToReceivingQty = control.value;

    if (!control.dirty) {
      return null;
    }
    if (addToReceivingQty === null || typeof addToReceivingQty === 'string') {
      return { value: 'Invalid value for Add to Received Quantity' };
    }
    if (typeof addToReceivingQty !== 'number') {
      return { value: 'Add to Received Quantity must be a number' };
    }
    if (addToReceivingQty < 0) {
      return {
        addToReceivingQty: 'Add to Received Quantity cannot be less than 0',
      };
    }

    return null;
  }

  private getFromService() :void {
    this.loading$ = this.purchaseOrderService.loading$;
  }

  private convertUnderscoreToCamelCase(
    obj :Record<string, any>,
  ) :Record<string, any> {
    if (typeof obj !== 'object' || obj === null) {
      return obj;
    }

    const camelCaseObj :Record<string, any> = {};

    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const camelCaseKey = key.replace(/_(\w)/g, (match, p1) => p1.toUpperCase());
        camelCaseObj[camelCaseKey] = this.convertUnderscoreToCamelCase(obj[key]);
      }
    }

    return camelCaseObj;
  }

  private isEmptyOrHasSpaces(value :string = '') :boolean {
    return value === null || value?.match(/^ *$/) !== null;
  }

  private loadDataFromService() :void {
    this.inventorySettings$ = this.settingsSvc.inventorySettings$;
  }

  private initInventorySettings() :void {
    this.inventorySettings$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((resp :any) => {
        if (resp && resp.id) {
          this.inventorySettings = JSON.parse(resp.value);
          this.inventorySettings.warehouse_id = parseInt(
            this.inventorySettings.warehouse_id,
          );
          this.getPickingWarehouse(this.inventorySettings.warehouse_id);
        } else {
          this.inventorySettings = null;
        }
      });
  }

  private getPickingWarehouse(pickingWarehouseId :number) :void {
    this.locationApiService
      .getById(pickingWarehouseId)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response :IWarehouse) => {
          this.pickingWarehouse = response;
          this.defaultPickingWarehouseLocation =
            response?.default_receiving_location;
          this.cd.detectChanges();
        },
        error: (error :any) => {},
      });
  }

  get lineItemsFormArray() :UntypedFormArray {
    return this.form.get('lineItems') as UntypedFormArray;
  }

  get dirtyValidLineItems() :AbstractControl[] {
    return this.lineItemsFormArray.controls.filter(
      (lineItem :AbstractControl) =>
        (lineItem.get('purchasePrice')?.dirty &&
          !lineItem.get('purchasePrice')?.invalid) ||
        (lineItem.get('purchaseQty')?.dirty &&
          !lineItem.get('purchaseQty')?.invalid) ||
        (lineItem.get('addToReceivingQty')?.dirty &&
          !lineItem.get('addToReceivingQty')?.invalid &&
          lineItem.value.addToReceivingQty > 0) ||
        (lineItem.get('lotId')?.dirty && !lineItem.get('lotId')?.invalid),
    );
  }

  get dirtyLineItemCount() :number {
    return this.dirtyValidLineItems?.length;
  }

  get rrLineItems() :AbstractControl[] {
    return this.lineItemsFormArray.controls.filter(
      (lineItem :AbstractControl) =>
        lineItem.get('addToReceivingQty')?.dirty &&
        !lineItem.get('addToReceivingQty')?.invalid,
    );
  }

  get rrLineItemCount() :number {
    return this.rrLineItems?.length;
  }

  get paginationLength() :number {
    return (this.pagination.current_page - 1) * this.pagination.per_page;
  }

  get paginatedLineItems() :any[] {
    return this.lineItemsFormArray.controls.slice(
      this.paginationLength,
      this.paginationLength + this.pagination.per_page,
    );
  }

  get checkedAllLineItems() :boolean {
    if (this.orders?.length <= 0) {
      return false;
    }

    return this.lineItemsFormArray.controls.every(
      (lineItem :AbstractControl) => lineItem.value.checked,
    );
  }

  get checkedSomeLineItems() :boolean {
    return (
      this.lineItemsFormArray.controls.some(
        (lineItem :AbstractControl) => lineItem.value.checked,
      ) && !this.checkedAllLineItems
    );
  }

  get checkedLineItems() :AbstractControl[] {
    return this.lineItemsFormArray.controls.filter(
      (lineItem :AbstractControl) => lineItem.value.checked,
    );
  }

  get checkedLineItemCount() :number {
    return this.checkedLineItems?.length;
  }

  get checkedAndlotTrackingItems() :AbstractControl[] {
    return this.checkedLineItems.filter(
      (lineItem :AbstractControl) => lineItem.value.hasLotTracking,
    );
  }

  get checkedLineItemCountHasLotTracking() :number {
    return (
      this.checkedLineItems.filter(
        (lineItem :AbstractControl) => lineItem.value.hasLotTracking,
      ).length || 0
    );
  }
}
